/**
 * Ask us — the phone launcher.
 *
 * Desktop never sees this: the live chat script loads there and draws its own
 * launcher, which is what people use. This is for phones, where the habit is
 * WhatsApp and Instagram, and where a chat window is the whole screen.
 *
 * The live chat is offered as one of the choices rather than loaded up front.
 * Nothing is fetched, nothing connects and no cookie is set until somebody
 * actually asks to chat here.
 *
 * And on either device it waits for cookie consent. Hive sets cookies and opens
 * a connection, so it is treated as a functional cookie: CookieYes is asked
 * first, and if the answer is no the script is not fetched at all. Accepting
 * later loads it there and then, without a reload — the shop has CookieYes,
 * and a consent banner that nothing honours is worse than none.
 */
(function () {
  'use strict';

  /* ---- cookie consent ------------------------------------------------
     CookieYes publishes getCkyConsent() and fires cookieyes_consent_update
     when somebody answers. If neither exists — the app removed, or blocked —
     the chat stays unloaded rather than loading unasked. */
  function consented(category) {
    if (category === 'none') return true;
    try {
      var c = window.getCkyConsent && window.getCkyConsent();
      return !!(c && c.categories && c.categories[category]);
    } catch (e) {
      return false;
    }
  }

  function whenConsented(category, run) {
    if (consented(category)) { run(); return; }

    var done = false;
    function go() {
      if (done || !consented(category)) return;
      done = true;
      clearInterval(poll);
      document.removeEventListener('cookieyes_consent_update', go);
      run();
    }

    /* The event covers somebody answering the banner now. The poll covers
       somebody who answered on an earlier visit: CookieYes loads after this
       script, so at this moment getCkyConsent does not exist yet, the answer
       reads as "no", and no update event is ever fired for a decision that was
       taken last week. Without the poll the chat never appeared for anyone who
       had already accepted — which is every returning customer. */
    document.addEventListener('cookieyes_consent_update', go);
    var waited = 0;
    var poll = setInterval(function () {
      waited += 400;
      if (waited > 20000) { clearInterval(poll); return; }
      go();
    }, 400);
  }

  /* Ask CookieYes to show its banner again, for somebody who has pressed chat
     and has not accepted the category it needs. */
  function askForConsent() {
    if (typeof window.revisitCkyConsent === 'function') {
      try { window.revisitCkyConsent(); return true; } catch (e) {}
    }
    return false;
  }

  function loadHive(key, then) {
    if (!key || window.__tcHiveLoading) { if (then) then(); return; }
    window.__tcHiveLoading = true;
    var s = document.createElement('script');
    s.src = 'https://hivehd.app/widget.js';
    s.setAttribute('data-widget-key', key);
    s.async = true;
    if (then) s.addEventListener('load', then);
    document.head.appendChild(s);
  }

  function setup(root) {
    if (root.__tcChat) return;
    root.__tcChat = true;

    var from = Number(root.getAttribute('data-desktop-from')) || 990;
    var onDesktop = window.matchMedia('(min-width: ' + from + 'px)');
    var key = root.getAttribute('data-hive-key');
    var category = root.getAttribute('data-consent-category') || 'functional';
    var open = root.querySelector('[data-tc-chat-open]');
    var sheet = root.querySelector('[data-tc-chat-sheet]');
    var close = root.querySelector('[data-tc-chat-close]');
    var web = root.querySelector('[data-tc-chat-web]');

    /*
     * Hive answers on every screen, not just the wide ones.
     *
     * This used to load Hive on desktop and put our own sheet in front of it on
     * a phone, on the grounds that Hive's chat window takes the whole screen
     * there. But Hive is where WhatsApp lives - the number is held in their
     * account, connected through Twilio - and our sheet only knows the channels
     * this theme has settings for. So a phone got web chat, Instagram and
     * Messenger, and the one channel most customers actually use was missing.
     *
     * Ours stays as the fallback for a shop with no Hive key, which is what it
     * was written for.
     */
    if (key) whenConsented(category, function () { loadHive(key); });

    if (!open || !sheet) return; // no channels configured; nothing to launch

    /* Hidden wherever Hive is doing the job, rather than never rendered, so a
       window resized across the breakpoint never ends up with two launchers
       arguing over the same corner. */
    function place() {
      var hiveHasIt = !!key;
      root.hidden = hiveHasIt || onDesktop.matches;
      if (root.hidden) show(false);
    }
    place();
    if (onDesktop.addEventListener) onDesktop.addEventListener('change', place);
    else onDesktop.addListener(place);

    function show(on) {
      sheet.hidden = !on;
      root.classList.toggle('is-open', on);
      open.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    open.addEventListener('click', function () { show(sheet.hidden); });
    if (close) close.addEventListener('click', function () { show(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) show(false);
    });
    document.addEventListener('click', function (e) {
      if (!sheet.hidden && !root.contains(e.target)) show(false);
    });

    if (web) {
      web.addEventListener('click', function () {
        if (!key || root.__hiveLoaded) { show(false); return; }

        /* Pressing "chat here" is a request for the service, not consent for
           its cookies. If the category has not been accepted, put CookieYes's
           own banner back in front of them and load the moment they agree. */
        if (!consented(category)) {
          var asked = askForConsent();
          whenConsented(category, function () { start(); });
          if (asked) show(false);
          return;
        }
        start();

        function start() {
          if (root.__hiveLoaded) return;
          root.__hiveLoaded = true;
          web.classList.add('is-loading');
          loadHive(key, function () {
            /* Once it is there, ask it to open if it will. If it only draws its
               own bubble, that is fine — the sheet closes and the bubble is
               what they tap. */
            setTimeout(function () {
              var api = window.HiveHD || window.Hive || window.hive;
              if (api && typeof api.open === 'function') { try { api.open(); } catch (err) {} }
              web.classList.remove('is-loading');
              show(false);
            }, 400);
          });
        }
      });
    }
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-tc-chat]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
